import { Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useState } from 'react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/_components/ui/card'
import { authClient } from '@/features/_lib/auth-client'

import { AuthLegalLinks } from '../../_components/auth-legal-links'
import type { CodeStepSubmitHandler } from './code-step'
import { CodeStep } from './code-step'
import type { EmailStepSubmitHandler } from './email-step'
import { EmailStep } from './email-step'
import type { PasswordStepSubmitHandler } from './password-step'
import { PasswordStep } from './password-step'
import { Success } from './success'

enum Step {
  Email,
  Code,
  Password,
  Success,
}

type ForgotPasswordFormProps = {
  defaultEmailValue?: string
}

export const ForgotPasswordForm = ({ defaultEmailValue }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState<string | undefined>()
  const [otp, setOtp] = useState<string | undefined>()
  const [step, setStep] = useState<Step>(Step.Email)

  const handleEmailStepSubmit: EmailStepSubmitHandler = async (data, setError) => {
    const { error } = await authClient.emailOtp.requestPasswordReset({ email: data.email })
    if (error) {
      setError('email', {
        type: 'manual',
        message: 'Não foi possível enviar o código. Tente novamente.',
      })
      return
    }
    setEmail(data.email)
    setStep(Step.Code)
  }

  const handleCodeStepSubmit: CodeStepSubmitHandler = async (data, setError) => {
    if (!email) {
      setStep(Step.Email)
      return
    }
    const { error } = await authClient.emailOtp.checkVerificationOtp({
      email,
      otp: data.code,
      type: 'forget-password',
    })
    if (error) {
      setError('code', { type: 'manual', message: 'Código inválido ou expirado.' })
      return
    }
    setOtp(data.code)
    setStep(Step.Password)
  }

  const handlePasswordStepSubmit: PasswordStepSubmitHandler = async (data, setError) => {
    if (!email || !otp) {
      setStep(Step.Email)
      return
    }
    const { error } = await authClient.emailOtp.resetPassword({
      email,
      otp,
      password: data.password,
    })
    if (error) {
      setError('password', {
        type: 'manual',
        message: 'Não foi possível redefinir a senha. Solicite um novo código.',
      })
      return
    }
    setStep(Step.Success)
  }

  const renderStep = (step: Step) => {
    return {
      [Step.Email]: (
        <EmailStep onSubmit={handleEmailStepSubmit} defaultEmailValue={defaultEmailValue} />
      ),
      [Step.Code]: <CodeStep onSubmit={handleCodeStepSubmit} />,
      [Step.Password]: <PasswordStep onSubmit={handlePasswordStepSubmit} />,
      [Step.Success]: <Success />,
    }[step]
  }

  const renderDescription = (step: Step) => {
    return {
      [Step.Email]: 'Digite seu e-mail cadastrado abaixo.',
      [Step.Code]: `Enviamos um código de acesso ao email ${email}. Insira o código abaixo para criar uma nova senha.`,
      [Step.Password]: 'Cadastre sua nova senha.',
      [Step.Success]: 'Sua nova senha foi cadastrada com sucesso!',
    }[step]
  }

  return (
    <Card className="w-full max-w-[420px] border-0 p-8 shadow-[0_12px_48px_rgba(26,33,52,0.11)]">
      <Link
        to="/login"
        className="mb-2 flex w-fit items-center gap-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft size={14} />
        Voltar
      </Link>

      <CardHeader className="p-0 pb-6">
        <CardTitle>{step === Step.Success ? 'Sucesso!' : 'Recupere sua senha'}</CardTitle>
        <CardDescription>{renderDescription(step)}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">{renderStep(step)}</CardContent>

      <AuthLegalLinks />
    </Card>
  )
}
